import type { Announcements, ScreenReaderInstructions, UniqueIdentifier } from "@dnd-kit/core";

import type { ShellTranslate } from "@/lib/shell/use-shell-translation";

/**
 * What a screen reader hears while a column (or a filter) is dragged: the
 * interface language and the item's display name.
 *
 * dnd-kit's defaults are English and speak raw ids: "Picked up draggable item
 * firstName. Draggable item firstName was moved over droppable area email."
 * Pass the result as a DndContext's `accessibility` prop; `nameOf` turns an
 * id into the name shown on screen.
 *
 * dnd-kit reports that an item is over its own place at once on pick-up, and
 * "Name was moved over Name" would replace "Picked up Name" before a screen
 * reader had read it, so that first report is silent. Any later return to its
 * own place is announced ("Name is back in its original position."): a
 * keyboard user who moves a column away and back must hear where it is.
 */
export function dragAccessibility(
  t: ShellTranslate,
  nameOf: (id: string) => string,
): { screenReaderInstructions: ScreenReaderInstructions; announcements: Announcements } {
  const name = (id: UniqueIdentifier) => nameOf(String(id));
  // The item just picked up, until dnd-kit's first report of where it is.
  let justPickedUp: UniqueIdentifier | null = null;
  return {
    screenReaderInstructions: { draggable: t("shell.table.drag.instructions") },
    announcements: {
      onDragStart: ({ active }) => {
        justPickedUp = active.id;
        return t("shell.table.drag.pickedUp", { name: name(active.id) });
      },
      onDragOver: ({ active, over }) => {
        const firstReport = justPickedUp === active.id;
        justPickedUp = null;
        if (!over) return t("shell.table.drag.notOver", { name: name(active.id) });
        if (over.id === active.id) {
          return firstReport ? undefined : t("shell.table.drag.backInPlace", { name: name(active.id) });
        }
        return t("shell.table.drag.movedOver", { name: name(active.id), over: name(over.id) });
      },
      onDragEnd: ({ active, over }) => {
        justPickedUp = null;
        return over && over.id !== active.id
          ? t("shell.table.drag.dropped", { name: name(active.id), over: name(over.id) })
          : t("shell.table.drag.droppedNowhere", { name: name(active.id) });
      },
      onDragCancel: ({ active }) => {
        justPickedUp = null;
        return t("shell.table.drag.cancelled", { name: name(active.id) });
      },
    },
  };
}
